/**
 * MerchantSafePayButton — initiates a MerchantSafePay session on mount and
 * immediately redirects the browser to the returned payment URL.
 *
 * Shows a spinner while the session is being created and an error message if
 * the request fails so the user can retry.
 */
import { useEffect, useState } from 'react'
import { Loader2, AlertCircle, RefreshCw } from 'lucide-react'
import { initiatePayment } from '@/services/paymentService'

interface MerchantSafePayButtonProps {
  invoiceId: number
  /** If true, navigate in the same window. False = new tab. */
  openInSameWindow?: boolean
  /** Called when the redirect is about to happen. */
  onRedirect?: (paymentUrl: string) => void
}

type State = 'idle' | 'loading' | 'redirecting' | 'error'

export function MerchantSafePayButton({
  invoiceId,
  openInSameWindow = false,
  onRedirect,
}: MerchantSafePayButtonProps) {
  const [state, setState] = useState<State>('idle')
  const [errorMessage, setErrorMessage] = useState('')

  async function createSessionAndRedirect() {
    setState('loading')
    setErrorMessage('')

    try {
      const result = await initiatePayment(invoiceId, { provider: 'merchantsafepay' })

      if (result.type !== 'redirect') {
        throw new Error('Unexpected response type from server.')
      }

      setState('redirecting')
      onRedirect?.(result.payment_url)

      if (openInSameWindow) {
        window.location.href = result.payment_url
      } else {
        window.open(result.payment_url, '_blank', 'noopener,noreferrer')
      }
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : 'Payment session creation failed. Please try again.'
      setErrorMessage(message)
      setState('error')
    }
  }

  // Auto-trigger on mount
  useEffect(() => {
    createSessionAndRedirect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceId])

  if (state === 'loading' || state === 'redirecting') {
    return (
      <div className="flex flex-col items-center gap-3 py-6">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <p className="text-sm text-gray-600">
          {state === 'loading' ? 'Creating payment session…' : 'Redirecting to payment page…'}
        </p>
      </div>
    )
  }

  if (state === 'error') {
    return (
      <div className="flex flex-col items-center gap-3 py-6">
        <div className="flex items-center gap-2 text-red-600">
          <AlertCircle className="h-5 w-5" />
          <p className="text-sm font-medium">{errorMessage}</p>
        </div>
        <button
          type="button"
          onClick={createSessionAndRedirect}
          className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition hover:bg-gray-50 min-h-[44px]"
        >
          <RefreshCw className="h-4 w-4" />
          Try again
        </button>
      </div>
    )
  }

  // idle — should not be visible for long since useEffect fires immediately
  return null
}
